/**
 * The single music lane under the scene strip. The bar spans the audible
 * window (offset → timeline end or source end); drag it to move the music
 * along the timeline, click it to open its controls.
 */

import { useRef } from 'react';
import { audioWindow, effectiveFades } from '@/lib/video/audioTrack';
import { useVideoStore } from '@/stores/videoStore';
import AudioImportButton from './AudioImportButton';

/** Offset drags snap to this grid so values stay readable. */
const OFFSET_SNAP_MS = 100;

interface AudioTrackLaneProps {
  pxPerSec: number;
  totalMs: number;
  selected: boolean;
  onSelect: (selected: boolean) => void;
}

export default function AudioTrackLane({ pxPerSec, totalMs, selected, onSelect }: AudioTrackLaneProps) {
  const track = useVideoStore((s) => s.video?.audio ?? null);
  const checkpoint = useVideoStore((s) => s.checkpoint);
  const updateAudio = useVideoStore((s) => s.updateAudio);
  const dragRef = useRef<{ startX: number; startMs: number; moved: boolean } | null>(null);

  if (!track) {
    return (
      <div className="vt-audio-lane vt-audio-lane-empty">
        <AudioImportButton />
      </div>
    );
  }

  const w = audioWindow(track, totalMs);
  const px = (ms: number) => (ms / 1000) * pxPerSec;
  const left = px(track.offsetMs);
  const width = w ? Math.max(8, px(w.endMs - w.startMs)) : 8;
  const fades = w ? effectiveFades(track, w) : { inMs: 0, outMs: 0 };

  return (
    <div className="vt-audio-lane">
      <div
        className={`vt-audio-clip ${selected ? 'active' : ''} ${w ? '' : 'out-of-range'}`}
        style={{ left, width }}
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        aria-label={`Music: ${track.name}`}
        title={`${track.name} · drag to move · click for volume, fades and trim`}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSelect(!selected);
        }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          dragRef.current = { startX: e.clientX, startMs: track.offsetMs, moved: false };
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          if (!d) return;
          const dx = e.clientX - d.startX;
          if (!d.moved) {
            if (Math.abs(dx) < 3) return;
            d.moved = true;
            checkpoint();
          }
          const raw = d.startMs + (dx / pxPerSec) * 1000;
          updateAudio({ offsetMs: Math.round(raw / OFFSET_SNAP_MS) * OFFSET_SNAP_MS }, { record: false });
        }}
        onPointerUp={(e) => {
          const d = dragRef.current;
          dragRef.current = null;
          e.currentTarget.releasePointerCapture(e.pointerId);
          if (d && !d.moved) onSelect(!selected);
        }}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
      >
        {fades.inMs > 0 && <span className="vt-audio-fade in" style={{ width: px(fades.inMs) }} aria-hidden />}
        {fades.outMs > 0 && <span className="vt-audio-fade out" style={{ width: px(fades.outMs) }} aria-hidden />}
        <span className="vt-audio-name">♪ {track.name}</span>
        {track.source === 'idb' && (
          <span className="vt-audio-badge" title="Stored on this device only; not backed up yet">
            local
          </span>
        )}
      </div>
    </div>
  );
}
